import React, { createContext, useContext, useState } from "react";

interface Video {
  id: string;
  title: string;
  artist: string;
  audioUrl: string;
  frames: string[];
  thumbnail: string;
  duration: number;
  views: number;
  likes: number;
  dislikes: number;
  uploadDate: string;
  userId: string;
  tags: string[];
}

interface Comment {
  id: string;
  videoId: string;
  userId: string;
  username: string;
  avatar: string;
  text: string;
  timestamp: string;
  likes: number;
  replies: Comment[];
}

interface VideoContextType {
  videos: Video[];
  comments: Comment[];
  currentVideo: Video | null;
  setCurrentVideo: (video: Video | null) => void;
  addComment: (videoId: string, text: string) => void;
  likeVideo: (videoId: string) => void;
  addVideo: (video: Omit<Video, "id" | "views" | "likes" | "dislikes" | "uploadDate">) => void;
}

const VideoContext = createContext<VideoContextType | null>(null);

export const VideoProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [currentVideo, setCurrentVideo] = useState<Video | null>(null);

  const addVideo = (videoData: Omit<Video, "id" | "views" | "likes" | "dislikes" | "uploadDate">) => {
    const newVideo: Video = {
      ...videoData,
      id: Date.now().toString(),
      views: 0,
      likes: 0,
      dislikes: 0,
      uploadDate: new Date().toISOString()
    };
    setVideos(prev => [newVideo, ...prev]);
  };

  const addComment = (videoId: string, text: string) => {
    const newComment: Comment = {
      id: Date.now().toString(),
      videoId,
      userId: "current-user",
      username: "Current User",
      avatar: "",
      text,
      timestamp: new Date().toISOString(),
      likes: 0,
      replies: []
    };
    setComments(prev => [...prev, newComment]);
  };

  const likeVideo = (videoId: string) => {
    setVideos(prev => prev.map(video => 
      video.id === videoId ? { ...video, likes: video.likes + 1 } : video
    ));
  };

  return (
    <VideoContext.Provider value={{
      videos,
      comments,
      currentVideo,
      setCurrentVideo,
      addComment,
      likeVideo,
      addVideo
    }}>
      {children}
    </VideoContext.Provider>
  );
};

export const useVideo = () => {
  const context = useContext(VideoContext);
  if (!context) throw new Error("useVideo must be used within VideoProvider");
  return context;
};
